import React from "react";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";
import {
  TrendingUp, DollarSign, Target, Award, BarChart3
} from "lucide-react";

import {
  leads,
  customers,
  bookings,
  packages
} from "../data/mockData";

const COLORS = ["#0A4D68", "#088395", "#F2A154", "#059669", "#7C3AED", "#EC4899"];

export default function Analytics() {

  const conversionRate =
    leads.length > 0
      ? ((leads.filter(l => l.status === "Won").length / leads.length) * 100).toFixed(1)
      : 0;

  const totalRevenue = bookings.reduce(
    (sum, b) => sum + (b.total_amount || 0),
    0
  );

  const avgBookingValue =
    bookings.length > 0 ? (totalRevenue / bookings.length).toFixed(0) : 0;

  // Revenue trend
  const revenueByMonth = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date();
    d.setMonth(d.getMonth() - i);
    const label = d.toLocaleString("default", { month: "short" });

    const revenue = bookings
      .filter(b => {
        const bd = new Date(b.created_at);
        return bd.getMonth() === d.getMonth() &&
               bd.getFullYear() === d.getFullYear();
      })
      .reduce((sum, b) => sum + (b.total_amount || 0), 0);

    revenueByMonth.push({ month: label, revenue });
  }

  const bookingsByStatus = Object.entries(
    bookings.reduce((acc, b) => {
      acc[b.status] = (acc[b.status] || 0) + 1;
      return acc;
    }, {})
  ).map(([name, value]) => ({ name, value }));

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold mb-6">Analytics</h1>

      {/* METRICS */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Metric title="Conversion Rate" value={`${conversionRate}%`} icon={Target} />
        <Metric title="Total Revenue" value={`₹${totalRevenue}`} icon={DollarSign} />
        <Metric title="Avg Booking" value={`₹${avgBookingValue}`} icon={BarChart3} />
        <Metric title="Active Packages" value={packages.length} icon={Award} />
      </div>

      {/* CHARTS */}
      <div className="grid md:grid-cols-2 gap-6">
        <ChartCard title="Revenue Trend">
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={revenueByMonth}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Line dataKey="revenue" stroke="#0A4D68" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Bookings by Status">
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={bookingsByStatus} dataKey="value" nameKey="name" outerRadius={90}>
                {bookingsByStatus.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}

function Metric({ title, value, icon: Icon }) {
  return (
    <div className="bg-white rounded-xl p-5 shadow">
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm text-gray-500">{title}</span>
        <Icon size={18} />
      </div>
      <h3 className="text-2xl font-bold">{value}</h3>
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="bg-white rounded-xl p-5 shadow">
      <h3 className="text-lg font-semibold mb-4">{title}</h3>
      {children}
    </div>
  );
}
